import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
} from "@tanstack/react-query";
import { Dialog } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { BarChart, StackedBarChart } from "@mui/icons-material";
import { Box } from "@mui/material";
import styled from "styled-components";

// Local imports
import { apiConfig } from "../../../../utils/apiConfig";
import {
  useAnnualReviewData,
  useReferenceData,
  useTaxData,
} from "../../../../hooks/useAnnualReviewData";
import SubPageLayout from "../../../../components/shared/SubPageLayout";
import IncomeAnalysisTable from "../../../../components/shared/IncomeAnalysisTable";
import ExpenseAnalysisTable from "../../../../components/expenses/ExpenseAnalysisTable";
import HouseholdDebtAnalysis from "../../../../components/domains/advisor-portal/HouseholdDebtAnalysis";
import MemberCard from "../../../../components/domains/advisor-portal/client-profile/household-information/MemberCard";
import OverviewCard from "../../../../components/domains/advisor-portal/client-profile/household-information/OverviewCard";
import { getAllAssetsWithPrimaryResidence } from "../../../../utils/assetHelpers";
import SidebarTemplate from "../../../../components/shared/SidebarTemplate";
import CallConfirmationModal from "../../../../components/domains/advisor-portal/CallConfirmationModal";
import { useDemo } from "../../../../contexts/DemoContext";
import DemoModal from "../../../../components/shared/DemoModal";
import MeetingBriefingRoom from "../../../../components/domains/advisor-portal/MeetingBriefingRoom";
import MeetingDebriefingRoom from "../../../../components/domains/advisor-portal/MeetingDebriefingRoom";
import logger from '../../../../utils/logger';
import { useTheme } from '../../../../contexts/ThemeContext';
import { ThemeProvider as StyledThemeProvider } from "../../../../theme/ThemeProvider";
import { ThemeProvider as ThemeContextProvider } from "../../../../contexts/ThemeContext";
import Modal from "../../../../components/shared/Modal";
import { Section } from '../../../../components/layout/Section';
import { Grid } from '../../../../components/layout/Grid';
import HouseholdAssetAnalysis from "../../../../components/domains/advisor-portal/HouseholdAssetAnalysis";
import HouseholdNetWorthCard from "../../../../components/domains/advisor-portal/HouseholdNetWorthCard";
import HouseholdTaxAnalysis from "../../../../components/domains/advisor-portal/HouseholdTaxAnalysis";
import TaxBracketInformation from "../../../../pages/financial-planning/tax-planning/TaxBracketInformation";
import DeductionsTable from "../../../../pages/financial-planning/tax-planning/DeductionsTable";

// Import demo data
import {
  DEMO_TRANSCRIPT,
  DEMO_NOTES_SUMMARY,
  DEMO_NOTES_UPDATES,
  DEMO_NOTES_TASKS,
  MODAL_TYPES,
  MODAL_TEMPLATES,
  DEMO_TIMING,
  normalizeTimeString
} from "../../../../demo/annualReviewDemoLogic";
import { demoMeetingDetails, demoFormData, demoPipelineData } from "../../../../demo/briefingRoomData";

// Constants
const SKIP_LEGACY_RESTORE = false; // Set this to false since we want to keep legacy restore for now
const CARD_BASE = "p-4 rounded-lg shadow-lg dark:shadow-none";

const DemoToolbar = ({ isPaused, onTogglePause, onStop }) => {
  const [isConfirmingStop, setIsConfirmingStop] = useState(false);

  const handleStopClick = () => {
    if (isConfirmingStop) {
      onStop();
      setIsConfirmingStop(false);
    } else {
      setIsConfirmingStop(true);
      // Auto-reset confirmation after 3 seconds
      setTimeout(() => setIsConfirmingStop(false), 3000);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        display: 'flex',
        gap: 8,
        zIndex: 2000,
        padding: '8px',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-green-500'}`} />
        <span className="text-sm font-medium text-gray-700">
          {isPaused ? 'Paused' : 'Playing'}
        </span>
      </div>
      <button
        onClick={onTogglePause}
        className={`px-3 py-1 rounded text-white transition-colors ${
          isPaused 
            ? 'bg-green-500 hover:bg-green-600' 
            : 'bg-yellow-500 hover:bg-yellow-600'
        }`}
      >
        {isPaused ? 'Resume' : 'Pause'}
      </button>
      <button
        onClick={handleStopClick}
        className={`px-3 py-1 rounded text-white transition-colors ${
          isConfirmingStop
            ? 'bg-red-700 hover:bg-red-800'
            : 'bg-red-600 hover:bg-red-700'
        }`}
      >
        {isConfirmingStop ? 'Confirm Stop' : 'Stop'}
      </button>
    </div>
  );
};

const CustomBarIcon = () => (
  <Box
    sx={{
      display: "flex",
      gap: 0.25,
      alignItems: "flex-end",
      height: 20,
      width: 28,
      bgcolor: "grey.100",
      p: 0.375,
      borderRadius: 1,
    }}
  >
    <Box
      sx={{
        width: "22%",
        height: "90%",
        bgcolor: "success.main",
        borderRadius: 0.5,
      }}
    />
    <Box
      sx={{
        width: "22%",
        height: "80%",
        bgcolor: "error.main",
        borderRadius: 0.5,
      }}
    />
    <Box
      sx={{
        width: "22%",
        height: "100%",
        bgcolor: "success.main",
        borderRadius: 0.5,
      }}
    />
    <Box
      sx={{
        width: "22%",
        height: "70%",
        bgcolor: "error.main",
        borderRadius: 0.5,
      }}
    />
  </Box>
);

const SectionHeader = ({ title, iconColor = "text-gray-600 dark:text-gray-400", Icon }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className="flex items-center gap-1">
      <h2 className={`text-base md:text-lg xl:text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
        {title}
      </h2>
      <Icon className={`h-5 w-5 md:h-6 md:w-6 ${iconColor}`} />
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const TaxTable = ({ data, title }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} shadow-md dark:shadow-none rounded-lg p-4 mb-6`}>
      <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>{title}</h3>
      <div className="overflow-x-auto">
        <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              {Object.keys(data[0]).map((header) => (
                <th
                  key={header}
                  className={`px-3 py-2 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500 dark:text-gray-500'} uppercase tracking-wider`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {data.map((row, idx) => (
              <tr key={idx}>
                {Object.values(row).map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className={`px-3 py-2 whitespace-nowrap text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-900 dark:text-gray-100'}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TaxTables = ({ selectedYear, onYearChange, availableYears, taxData }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} shadow-md dark:shadow-none rounded-lg p-6 mb-6`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>Tax Reference Tables</h3>
        <div className="flex items-center space-x-2">
          <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>Tax Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            className={`rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-surface-default dark:bg-surface-default text-gray-900 dark:text-gray-100'}`}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      {taxData && Object.entries(taxData).map(([key, data]) => (
        data && data[selectedYear] ? (
          <TaxTable 
            key={key} 
            data={data[selectedYear]} 
            title={`${selectedYear} ${key.replace(/_/g, ' ').toLowerCase()}`} 
          />
        ) : null
      ))}
    </div>
  );
};

const INCOME_TYPES_DATA = [
  { income_type_id: 1, income_type_name: "Social Security", is_other: false },
  { income_type_id: 2, income_type_name: "Pension", is_other: false },
  {
    income_type_id: 3,
    income_type_name: "Retirement Account Distributions (1099-R)",
    is_other: false,
  },
  { income_type_id: 4, income_type_name: "W-2 Wage Income", is_other: false },
  { income_type_id: 5, income_type_name: "1099 Income", is_other: false },
  { income_type_id: 6, income_type_name: "Dividends", is_other: false },
  {
    income_type_id: 7,
    income_type_name: "Long Term Capital Gain",
    is_other: false,
  },
  {
    income_type_id: 8,
    income_type_name: "Short Term Capital Gain",
    is_other: false,
  },
  { income_type_id: 9, income_type_name: "Royalty Income", is_other: false },
  { income_type_id: 10, income_type_name: "Interest Income", is_other: false },
  { income_type_id: 11, income_type_name: "Inheritance", is_other: false },
  { income_type_id: 12, income_type_name: "Other", is_other: true },
  { income_type_id: 13, income_type_name: "Rental Income", is_other: false },
  { income_type_id: 14, income_type_name: "Art Sale", is_other: false },
  {
    income_type_id: 15,
    income_type_name: "Grievance Settlement",
    is_other: false,
  },
  {
    income_type_id: 16,
    income_type_name: "Military Retirement",
    is_other: false,
  },
  { income_type_id: 17, income_type_name: "Wife Income", is_other: false },
  { income_type_id: 23, income_type_name: "Investment", is_other: false },
];

const HighlightableSection = styled.div`
  position: relative;
  transition: all 0.3s ease-in-out;
  border-radius: 4px;
  padding: 0.5rem;

  @media (min-width: 768px) {
    border-radius: 6px;
    padding: 0.75rem;
  }

  @media (min-width: 1280px) {
    border-radius: 8px;
    padding: 1rem;
  }

  &.highlight-glow {
    outline: 2px solid #10b981;
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
    z-index: 10;
    background: rgba(16, 185, 129, 0.05);

    @media (min-width: 768px) {
      outline: 2.5px solid #10b981;
    }

    @media (min-width: 1280px) {
      outline: 3px solid #10b981;
    }
  }
`;

// Add styled component for the sidebar wrapper
const SidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  z-index: 100001;
  isolation: isolate;
  pointer-events: ${props => props.$demoActive ? 'none' : 'auto'};

  & > * {
    height: 100vh;
    box-shadow: ${props => props.theme.isDarkMode ? 'none' : '-2px 0 10px rgba(0, 0, 0, 0.1)'};
  }

  /* Ensure the sidebar stays clear of any overlays */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
  }
`;

const TaxContainer = styled.div`
  background: ${({ theme }) => theme.isDarkMode ? '#1F2937' : '#FFFFFF'};
  border-radius: 0.375rem;
  box-shadow: ${({ theme }) => theme.isDarkMode ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'};
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const TaxTitle = styled.h3`
  color: ${({ theme }) => theme.isDarkMode ? '#F3F4F6' : '#111827'};
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? '#374151' : '#E5E7EB'};
  flex-shrink: 0;
`;

const TaxTableWrapper = styled.div`
  padding: 0.375rem;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;

  /* Ensure tables fill their containers properly */
  & > * {
    width: 100%;
  }

  /* Make tables more compact */
  table {
    font-size: 0.75rem;
    width: 100%;
  }

  th, td {
    padding: 0.25rem 0.375rem;
    white-space: nowrap;
  }
`;

const StyledTaxTable = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  /* Tighten spacing for HouseholdTaxAnalysis */
  .tax-analysis-row {
    margin-bottom: 0.125rem;
  }
  
  .tax-analysis-label {
    font-size: 0.75rem;
    margin-bottom: 0.125rem;
  }
  
  .tax-analysis-input {
    height: 1.5rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
  }

  /* Make tables more compact */
  table {
    font-size: 0.75rem;
  }

  th, td {
    padding: 0.25rem 0.375rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? '#374151' : '#E5E7EB'};
  flex-shrink: 0;
`;

const YearSelect = styled.select`
  padding: 0.125rem 0.375rem;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgb(75, 85, 99)' : 'rgb(229, 231, 235)'};
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.isDarkMode ? 'rgb(31, 41, 55)' : 'white'};
  color: ${({ theme }) => theme.isDarkMode ? 'rgb(229, 231, 235)' : 'rgb(17, 24, 39)'};
  font-size: 0.75rem;
  margin-left: 0.5rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.isDarkMode ? 'rgb(99, 102, 241)' : 'rgb(79, 70, 229)'};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.isDarkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(79, 70, 229, 0.1)'};
  }
`;

// Add helper function at the top level
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const AnnualReviewProfile = () => {
  const { householdId, version, year } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isDemoMode,
    demoStep,
    isRecording,
    isPaused,
    transcript,
    activeModal,
    pauseDemo,
    resumeDemo,
    stopDemo,
    setIsPaused,
    setIsRecording,
    setSelectedJourney,
    onModalAction,
    onAction
  } = useDemo();
  const { theme, isDarkMode } = useTheme();

  // Add mount/unmount logging
  useEffect(() => {
    console.log('📋 AnnualReviewProfile mounted', { 
      demoStep, 
      isDemoMode,
      locationState: location.state
    });
    return () => console.log('📋 AnnualReviewProfile unmounting');
  }, [demoStep, isDemoMode, location.state]);

  // Add proper demo state advancement
  useEffect(() => {
    // Only advance demo if just navigated for demo AND not already started
    if (isDemoMode && demoStep === 0) {
      console.log('🎯 Advancing initial demo step after mount');
      onAction({ type: 'START_DEMO' });
    }
  }, [isDemoMode, demoStep, onAction]);

  // Demo state
  const [demoCompleted, setDemoCompleted] = useState(false);
  const [demoCompletionTime, setDemoCompletionTime] = useState(null);
  const [showSmartDataPopup, setShowSmartDataPopup] = useState(false);
  const [showRecordingToolbar, setShowRecordingToolbar] = useState(false);
  const [showBriefingRoom, setShowBriefingRoom] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [notesSummary, setNotesSummary] = useState("");
  const [notesUpdates, setNotesUpdates] = useState([]);
  const [notesTasks, setNotesTasks] = useState([]);
  const [activeTab, setActiveTab] = useState("transcript");
  const [currentModalType, setCurrentModalType] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeModalData, setActiveModalData] = useState(null);
  const [showDebriefingRoom, setShowDebriefingRoom] = useState(false);
  const [isDemoLocked, setIsDemoLocked] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // Add hasInitDemo ref at the top
  const hasInitDemo = useRef(false);

  const {
    data: formData,
    isLoading: isLoadingForm,
    error: formError,
  } = useAnnualReviewData(householdId, year, version, isDemoMode);

  const { data: referenceData, isLoading: isLoadingRef } = useReferenceData();
  const { data: taxData, isLoading: isLoadingTax } = useTaxData();

  const loading = isLoadingForm || isLoadingRef || isLoadingTax;
  const [error, setError] = useState(formError?.message || formError);

  // Update error state when formError changes
  useEffect(() => {
    setError(formError?.message || formError);
  }, [formError]);

  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState(year);
  const [formState, setFormState] = useState({
    personalInfo: {
      prefix: "",
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      gender: "",
      dateOfBirth: "",
      maritalStatus: "",
      email: "",
      phoneNumber: "",
      phoneType: "",
      alternatePhone: "",
      alternatePhoneType: "",
      ssn: "",
    },
    spouseInfo: {
      prefix: "",
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      preferredName: "",
      dateOfBirth: "",
      ssn: "",
      gender: "",
      email: "",
      phoneNumber: "",
      sharedEmail: false,
      phoneType: null,
      alternatePhone: "",
      alternatePhoneType: null,
      dateOfDeath: "",
      isTrustedContact: false,
    },
    address: {
      street: "",
      unit: "",
      city: "",
      state: "",
      zipCode: "",
      isMailingDifferent: false,
      mailingAddress: {
        street: "",
        unit: "",
        city: "",
        state: "",
        zipCode: "",
      },
    },
    taxInfo: { filingStatus: "", filingState: "" },
    marriageInfo: {
      marriageDate: "",
      divorceDate: "",
      cityOfMarriage: "",
      stateOfMarriage: "",
    },
  });
  const [familyCount, setFamilyCount] = useState({ total: 0, dependents: 0 });
  const [referenceDataState, setReferenceDataState] = useState({
    prefixes: [],
    suffixes: [],
    genderTypes: [],
    maritalStatusTypes: [],
    taxFilingStatuses: [],
    filingStates: [],
  });
  const [pipelineData, setPipelineData] = useState({
    primaryMember: { stages: [], currentStage: null },
    spouse: { stages: [], currentStage: null },
  });
  const [isMeetingDetailsOpen, setIsMeetingDetailsOpen] = useState(false);
  const [meetingDetails] = useState({
    date: "2023-12-15",
    time: "14:00",
    videoLink: "https://meet.google.com/abc-defg-hij",
    status: "Scheduled",
  });
  const [isHouseholdInfoExpanded, setIsHouseholdInfoExpanded] = useState(true);

  // Ref for managing playback timeouts
  const playbackTimeoutRef = useRef(null);

  // Initial setup effect - runs once
  useEffect(() => {
    // Already initialized? -> bail out
    if (hasInitDemo.current) return;

    // Check if we're coming from a navigation that explicitly requests demo mode
    const shouldStartDemo = location.state?.startDemo;
    const isResumingDemo = location.state?.resumeDemo;

    if (isDemoMode && !isResumingDemo) {
      console.log('🎯 Setting up fresh demo');
      setShowBriefingRoom(true);
    } else if (shouldStartDemo && !isDemoMode) {
      // Only initialize if explicitly requested via navigation
      console.log('🎯 Initializing demo from navigation');
      setShowBriefingRoom(true);  // Set this first
      onAction({ type: 'START_DEMO' });
      // Clear the startDemo flag from history to prevent re-triggering on refresh
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Mark as initialized if we're in demo mode
    if (isDemoMode) {
      hasInitDemo.current = true;
    }
  }, [isDemoMode, location.state, onAction]); // Remove demoStep from dependencies

  // Effect to handle sidebar expansion ONLY during AI tool introduction
  // This is the ONLY place where sidebar should auto-expand during demo
  useEffect(() => {
    if (!activeModal) return;
    if (activeModal.type === MODAL_TYPES.AI_SIDEBAR_INTRODUCTION) {
      setSidebarCollapsed(false);
    }
  }, [activeModal?.type]);

  // Effect to handle demo state changes
  useEffect(() => {
    if (!isDemoMode) {
      setShowBriefingRoom(false);
    }
  }, [isDemoMode]);

  useEffect(() => {
    if (!formData) return;
    
    // Remove noisy debug logs
    // console.log("💥 formData.members:", formData?.members);
    // console.log("💥 Full formData:", formData);
    // console.log("💥 isDemoMode:", isDemoMode);
    // console.log("💥 householdId:", householdId);
    // console.log("💥 year:", year);
    // console.log("💥 version:", version);

    // Keep any important logic here
    if (formData.members) {
      setFamilyCount({
        total: formData.members.length,
        dependents: formData.members.filter(m => m.is_dependent).length
      });
    }
  }, [formData, isDemoMode, householdId, year, version]);

  useEffect(() => {
    if (formData && !loading) {
      // Initialize household member data
      if (formData.members && formData.members.length > 0) {
        const primaryMember = formData.members[0];

        // Set main member data
        setFormState((prev) => ({
          ...prev,
          personalInfo: {
            ...prev.personalInfo,
            firstName: primaryMember.firstName || "",
            middleName: primaryMember.middleName || "",
            lastName: primaryMember.lastName || "",
            dateOfBirth: primaryMember.dateOfBirth || "",
            email: primaryMember.contact?.email || "",
            phoneNumber: primaryMember.contact?.phone || "",
            maritalStatus:
              primaryMember.maritalStatus ||
              (formData.members.length > 1 ? "2" : "1"), // Default to married if spouse exists
          },
        }));

        // Set spouse data if available
        if (formData.members.length > 1) {
          const spouse = formData.members[1];
          setFormState((prev) => ({
            ...prev,
            spouseInfo: {
              ...prev.spouseInfo,
              firstName: spouse.firstName || "",
              middleName: spouse.middleName || "",
              lastName: spouse.lastName || "",
              dateOfBirth: spouse.dateOfBirth || "",
              email: spouse.contact?.email || "",
              phoneNumber: spouse.contact?.phone || "",
            },
          }));
        }

        // Set tax info data from both sources
        if (formData.taxInfo) {
          setFormState((prev) => ({
            ...prev,
            taxInfo: {
              filingStatus:
                formData.taxInfo.tax_filing_status_id?.toString() ||
                formData.taxInfo.filingStatus?.toString() ||
                (formData.members?.length > 1 ? "2" : "1"), // Default to married if spouse exists
              filingState:
                formData.taxInfo.filing_state_id?.toString() ||
                formData.taxInfo.filingState?.code?.toString() ||
                formData.taxInfo.filingState?.toString() ||
                "",
            },
          }));
        }

        // Update family count
        setFamilyCount({
          total: formData.members.length,
          dependents: formData.members.filter((m) => m.isDependent).length,
        });
      }
    }
  }, [formData, loading]);

  useEffect(() => {
    const fetchHouseholdInfoData = async () => {
      if (!householdId || loading) return;

      try {
        const primaryUserId = formData?.members?.[0]?.userId;
        if (!primaryUserId) {
          logger.info("No primary user ID found in form data");
          return;
        }

        logger.info("🛠️ Starting household info fetch:", {
          householdId,
          primaryUserId,
          isDemoMode,
          loading
        });
        
        const householdInfoResponse = await apiConfig.api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${primaryUserId}/household-information-direct`,
        );

        logger.info("📥 Household info response:", {
          data: householdInfoResponse.data,
          status: householdInfoResponse.status
        });

        if (householdInfoResponse.data) {
          const clientData = householdInfoResponse.data;

          logger.info("🔍 Processing client data:", {
            gender: {
              id: clientData.gender_type_id,
              type: clientData.genderType
            },
            maritalStatus: {
              id: clientData.marital_status_id,
              info: clientData.maritalInfo || clientData.MaritalInformation
            },
            address: clientData.address,
            taxInfo: formData?.taxInfo
          });

          // Update form state with client info, ensuring all fields are properly mapped
          setFormState((prev) => {
            const newState = JSON.parse(JSON.stringify(prev));

            // Personal Info
            newState.personalInfo = {
              ...newState.personalInfo,
              prefix: clientData.Name?.prefix || "",
              firstName: clientData.Name?.first_name || "",
              middleName: clientData.Name?.middle_name || "",
              lastName: clientData.Name?.last_name || "",
              suffix: clientData.Name?.suffix || "",
              gender: clientData.gender_type_id?.toString() || "",
              maritalStatus: clientData.marital_status_id?.toString() || "",
              email: clientData.ContactInformation?.email || "",
              phoneNumber: clientData.ContactInformation?.primary_number || "",
              phoneType: clientData.ContactInformation?.primary_number_type || "",
              alternatePhone: clientData.ContactInformation?.secondary_number || "",
              alternatePhoneType: clientData.ContactInformation?.secondary_number_type || "",
              ssn: clientData.ssn || "",
            };

            // Marriage Info
            newState.marriageInfo = {
              marriageDate: clientData.maritalInfo?.marriageDate || clientData.MaritalInformation?.marriage_date || "",
              divorceDate: clientData.maritalInfo?.divorceDate || clientData.MaritalInformation?.divorce_date || "",
              cityOfMarriage: clientData.maritalInfo?.cityOfMarriage || clientData.MaritalInformation?.city_of_marriage || "",
              stateOfMarriage: clientData.maritalInfo?.stateOfMarriage?.code || clientData.maritalInfo?.stateOfMarriage || clientData.MaritalInformation?.FilingState?.state_code || "",
            };

            // Tax Info - preserve existing tax info if available
            if (formData?.taxInfo) {
              newState.taxInfo = {
                filingStatus: formData.taxInfo.tax_filing_status_id?.toString() || formData.taxInfo.filingStatus?.toString() || "",
                filingState: formData.taxInfo.filing_state_id?.toString() || formData.taxInfo.filingState?.code?.toString() || formData.taxInfo.filingState?.toString() || "",
              };
            }

            logger.info("📝 Updated form state:", {
              personalInfo: newState.personalInfo,
              marriageInfo: newState.marriageInfo,
              taxInfo: newState.taxInfo
            });

            return newState;
          });

          // Fetch address data
          try {
            const primaryResponse = await apiConfig.api.get(
              `/advisor-portal/client-database/household-profile/${householdId}/address-by-type?type=1`,
            );
            const primaryAddress = primaryResponse.data;
            logger.info("Primary address found:", primaryAddress);

            // Update address in form state with correct field mapping
            setFormState((prev) => ({
              ...prev,
              address: {
                ...prev.address,
                street: primaryAddress.address_line1 || "",
                unit: primaryAddress.address_line2 || "",
                city: primaryAddress.city || "",
                state: primaryAddress.state || "",
                zipCode: primaryAddress.zip || "",
              },
            }));

            // Get mailing address if different
            try {
              const mailingResponse = await apiConfig.api.get(
                `/advisor-portal/client-database/household-profile/${householdId}/address-by-type?type=5`,
              );
              const mailingAddress = mailingResponse.data;
              logger.info("Mailing address found:", mailingAddress);

              if (mailingAddress) {
                setFormState((prev) => ({
                  ...prev,
                  address: {
                    ...prev.address,
                    isMailingDifferent: true,
                    mailingAddress: {
                      street: mailingAddress.address_line1 || "",
                      unit: mailingAddress.address_line2 || "",
                      city: mailingAddress.city || "",
                      state: mailingAddress.state || "",
                      zipCode: mailingAddress.zip || "",
                    },
                  },
                }));
              }
            } catch (err) {
              if (err.response?.status !== 404) {
                logger.error("Error fetching mailing address:", err);
              }
            }
          } catch (err) {
            logger.error("Error fetching primary address:", err);
          }

          // Fetch spouse data if exists
          if (clientData.spouse_id) {
            try {
              const spouseResponse = await apiConfig.api.get(
                `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${clientData.spouse_id}/household-information-direct`,
              );

              if (spouseResponse.data) {
                const spouseData = spouseResponse.data;
                logger.info("Spouse data found:", spouseData);

                setFormState((prev) => ({
                  ...prev,
                  spouseInfo: {
                    prefix: spouseData.Name?.prefix || "",
                    firstName: spouseData.Name?.first_name || "",
                    middleName: spouseData.Name?.middle_name || "",
                    lastName: spouseData.Name?.last_name || "",
                    suffix: spouseData.Name?.suffix || "",
                    preferredName: spouseData.Name?.preferred_name || "",
                    dateOfBirth: spouseData.date_of_birth || "",
                    ssn: spouseData.ssn || "",
                    gender: spouseData.gender_type_id?.toString() || "",
                    email: spouseData.ContactInformation?.email || "",
                    phoneNumber: spouseData.ContactInformation?.primary_number || "",
                    phoneType: spouseData.ContactInformation?.primary_number_type || "",
                    alternatePhone: spouseData.ContactInformation?.secondary_number || "",
                    alternatePhoneType: spouseData.ContactInformation?.secondary_number_type || "",
                    dateOfDeath: "",
                    isTrustedContact: false,
                  },
                }));
              }
            } catch (err) {
              logger.error("Error fetching spouse data:", err);
            }
          }
        }
      } catch (error) {
        logger.error("Error fetching household information:", error);
        setError("Failed to load household information");
      }
    };

    fetchHouseholdInfoData();
  }, [isDemoMode, householdId, formData, loading]);

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        logger.info("🔄 Starting reference data fetch");
        
        // Fetch reference data from API endpoints
        const [prefixesRes, suffixesRes, genderTypesRes, maritalStatusRes] =
          await Promise.all([
            apiConfig.api.get(
              "/advisor-portal/client-database/household-profile/reference-data/name-prefixes",
            ),
            apiConfig.api.get(
              "/advisor-portal/client-database/household-profile/reference-data/name-suffixes",
            ),
            apiConfig.api.get(
              "/advisor-portal/client-database/household-profile/reference-data/gender-types",
            ),
            apiConfig.api.get(
              "/advisor-portal/client-database/household-profile/reference-data/marital-status-types",
            ),
          ]);

        logger.info("📥 Reference data responses:", {
          prefixes: prefixesRes.data?.length || 0,
          suffixes: suffixesRes.data?.length || 0,
          genderTypes: genderTypesRes.data?.length || 0,
          maritalStatus: maritalStatusRes.data?.length || 0
        });

        // Set base reference data
        setReferenceDataState((prev) => {
          const newState = {
            ...prev,
            prefixes: prefixesRes.data || [],
            suffixes: suffixesRes.data || [],
            genderTypes: genderTypesRes.data || [],
            maritalStatusTypes: maritalStatusRes.data || [],
          };
          
          logger.info("🔄 Updating reference data state:", {
            prefixesCount: newState.prefixes.length,
            suffixesCount: newState.suffixes.length,
            genderTypesCount: newState.genderTypes.length,
            maritalStatusCount: newState.maritalStatusTypes.length
          });
          
          return newState;
        });

        // Now fetch tax info separately to handle potential 404 errors
        try {
          const taxInfoResponse = await apiConfig.api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/tax-info`,
          );
          logger.info("Tax info from API:", taxInfoResponse.data);

          // Set default values from our database query
          const defaultFilingStatus = "Married Filing Jointly";
          const defaultState = "MI";

          // Find matching filing status ID from the API response
          const matchingStatus = taxInfoResponse.data?.taxFilingStatuses?.find(
            (status) => status.name === (formData?.taxInfo?.filingStatus || defaultFilingStatus)
          );

          // Find matching state ID from the API response
          const matchingState = taxInfoResponse.data?.filingStates?.find(
            (state) => state.code === (formData?.taxInfo?.filingState?.code || formData?.taxInfo?.filingState || defaultState)
          );

          logger.info("Setting tax info with matched IDs:", {
            filingStatus: {
              value: matchingStatus?.id?.toString() || "",
              matched: matchingStatus?.name,
              original: formData?.taxInfo?.filingStatus,
            },
            filingState: {
              value: matchingState?.id?.toString() || "",
              matched: matchingState?.code,
              original: formData?.taxInfo?.filingState?.code || formData?.taxInfo?.filingState,
            },
          });

          // Update reference data state with tax info
          setReferenceDataState((prev) => {
            const newState = {
              ...prev,
              taxFilingStatuses: taxInfoResponse.data?.taxFilingStatuses || [],
              filingStates: taxInfoResponse.data?.filingStates || [],
            };

            logger.info("🔄 Updated reference data with tax info:", {
              taxFilingStatusesCount: newState.taxFilingStatuses.length,
              filingStatesCount: newState.filingStates.length
            });

            return newState;
          });

          // Update form state with matched IDs
          setFormState((prev) => {
            const newState = {
              ...prev,
              taxInfo: {
                filingStatus: matchingStatus?.id?.toString() || 
                  taxInfoResponse.data?.taxFilingStatuses?.find(s => s.name === defaultFilingStatus)?.id?.toString() || "",
                filingState: matchingState?.id?.toString() || 
                  taxInfoResponse.data?.filingStates?.find(s => s.code === defaultState)?.id?.toString() || "",
              }
            };

            logger.info("🔄 Updated form state with tax info:", {
              filingStatus: newState.taxInfo.filingStatus,
              filingState: newState.taxInfo.filingState
            });

            return newState;
          });
        } catch (taxError) {
          logger.error("Error fetching tax info:", taxError);
          // If we failed to get tax info, try using data from formData as fallback
          if (formData?.taxInfo) {
            setReferenceDataState((prev) => ({
              ...prev,
              taxFilingStatuses: formData.taxInfo.taxFilingStatuses || [],
              filingStates: formData.taxInfo.filingStates || [],
            }));
          }
        }
      } catch (error) {
        logger.error("Error fetching reference data:", error);
        setError("Failed to load reference data");
      }
    };

    fetchReferenceData();
  }, [isDemoMode, householdId, formData]);

  useEffect(() => {
    const fetchPipelineData = async () => {
      if (!householdId || loading) return;

      try {
        const primaryUserId = formData?.members?.[0]?.userId;
        if (!primaryUserId) return;

        // Fetch pipeline data for primary member
        try {
          const [stagesResponse, statusResponse] = await Promise.all([
            apiConfig.api.get(
              `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${primaryUserId}/pipeline/stages`,
            ),
            apiConfig.api.get(
              `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${primaryUserId}/pipeline`,
            ),
          ]);

          setPipelineData((prev) => ({
            ...prev,
            primaryMember: {
              stages: stagesResponse.data || [],
              currentStage: statusResponse.data || null,
            },
          }));
        } catch (err) {
          logger.error("Error fetching pipeline data:", err);
        }
      } catch (error) {
        logger.error("Failed to fetch pipeline data:", error);
      }
    };

    fetchPipelineData();
  }, [isDemoMode, householdId, formData, loading]);

  useEffect(() => {
    const loadTaxData = async () => {
      try {
        logger.info("Loading tax data...");
        const response = await fetch("/data/taxData.json");
        if (!response.ok) {
          throw new Error(
            `Failed to fetch tax data: ${response.status} ${response.statusText}`,
          );
        }
        const data = await response.json();
        logger.info("Tax Data Loaded:", data);

        if (!data.TAX_BRACKETS || !data.DEDUCTIONS) {
          throw new Error(
            "Invalid tax data format: missing TAX_BRACKETS or DEDUCTIONS",
          );
        }

        queryClient.setQueryData(["taxData"], data);
        if (data.TAX_BRACKETS[year]) {
          setSelectedTaxYear(year);
        } else {
          const availableYears = Object.keys(data.TAX_BRACKETS).sort(
            (a, b) => b - a,
          );
          if (availableYears.length > 0) {
            logger.info(
              `Tax data for ${year} not found, using latest year:`,
              availableYears[0],
            );
            setSelectedTaxYear(availableYears[0]);
          }
        }
      } catch (error) {
        logger.error("Error loading tax data:", error);
        queryClient.setQueryData(["taxData"], {
          TAX_BRACKETS: {},
          DEDUCTIONS: {},
        });
      }
    };

    loadTaxData();
  }, [year]);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const response = await apiConfig.api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/versions`,
        );
        if (response.data && response.data.versions) {
          const versions = response.data.versions[year] || [];
          setAvailableVersions(versions);
        }
      } catch (error) {
        logger.error("Error fetching versions:", error);
        setAvailableVersions([]);
      }
    };

    fetchVersions();
  }, [householdId, year]);

  useEffect(() => {
    if (location.state) {
      if (location.state.expandSidebar || location.state.preserveSidebar) {
        setSidebarCollapsed(false);
      }
      if (location.state.continueDemo && typeof location.state.demoStep === 'number') {
        onAction({ type: 'CONTINUE_DEMO', data: { step: location.state.demoStep } });
      }

    }
  }, [location.state]);

  const handleAssetUpdate = useCallback(
    (updatedData) => {
      queryClient.setQueryData(
        { queryKey: ["annualReview", householdId, year, version] },
        (oldData) => ({
          ...oldData,
          assets: updatedData.assets,
          primaryResidenceValue: updatedData.primaryResidenceValue,
        }),
      );
      // Also update local formState to ensure both cards are in sync
      setFormState((prev) => ({
        ...prev,
        assets: updatedData.assets,
        primaryResidenceValue: updatedData.primaryResidenceValue,
      }));
    },
    [householdId, year, version],
  );

  const handleIncomeUpdate = useCallback(
    (updatedIncomes) => {
      queryClient.setQueryData(
        { queryKey: ["annualReview", householdId, year, version] },
        (oldData) => ({
          ...oldData,
          incomes: updatedIncomes,
        }),
      );
    },
    [householdId, year, version],
  );

  const handleDebtUpdate = useCallback(
    (updatedDebts) => {
      queryClient.setQueryData(
        { queryKey: ["annualReview", householdId, year, version] },
        (oldData) => ({
          ...oldData,
          debts: updatedDebts,
        }),
      );
    },
    [householdId, year, version],
  );

  const handleStatusUpdate = useCallback(
    (statusUpdates) => {
      queryClient.setQueryData(
        { queryKey: ["annualReview", householdId, year, version] },
        (oldData) => ({
          ...oldData,
          notesStatus: statusUpdates,
          rothConversionDesire: statusUpdates.rothConversion,
          interestedInPortfolioAnalysis: statusUpdates.portfolioAnalysis,
          preferredReviewMedium: statusUpdates.preferredMedium,
        }),
      );
    },
    [householdId, year, version],
  );

  const handleFieldChange = useCallback((section, field, subfield, value) => {
    logger.info("🔄 Field change:", { section, field, subfield, value });
    
    setFormState((prevState) => {
      const newState = JSON.parse(JSON.stringify(prevState));

      if (
        section === "personalInfo" ||
        section === "spouseInfo" ||
        section === "taxInfo"
      ) {
        newState[section][field] = value;
      } else if (section === "address") {
        if (subfield) {
          if (field === "mailingAddress") {
            newState.address.mailingAddress[subfield] = value;
          } else {
            newState.address[field][subfield] = value;
          }
        } else {
          newState.address[field] = value;
        }
      } else if (section === "marriageInfo") {
        newState.marriageInfo[field] = value;
      } else if (field && !subfield) {
        if (section === "maritalStatus") {
          newState.personalInfo.maritalStatus = field;
        } else if (section === "filingStatus") {
          newState.taxInfo.filingStatus = field;
        } else if (section === "filingState") {
          newState.taxInfo.filingState = field;
        }
      }

      logger.info("📝 Updated form state:", {
        section,
        newValue: newState[section]
      });

      return newState;
    });
  }, []);

  const handleTaxInfoUpdate = useCallback(
    (updatedTaxInfo) => {
      queryClient.setQueryData(
        ["annualReview", householdId, year, version],
        (oldData) => ({
          ...oldData,
          taxInfo: updatedTaxInfo,
        }),
      );
    },
    [householdId, year, version],
  );

  const navigateProfile = (direction) => {
    const newVersion = parseInt(version) + direction;
    navigate(
      `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${newVersion}`,
    );
  };

  const canNavigateBack = () => parseInt(version) > 1;
  const canNavigateForward = () => {
    const nextVersion = parseInt(version) + 1;
    return availableVersions.includes(nextVersion);
  };

  const openInNewWindow = () => {
    window.open(
      `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}`,
      "_blank",
    );
  };

  const handleSaveProfile = () => {
    logger.info("Saving profile:", formData);
    // TODO: Implement save functionality
  };

  const breadcrumbItems = [
    { label: "Advisor Portal", path: "/advisor-portal" },
    { label: "Client Database", path: "/advisor-portal/client-database" },
    {
      label: "Household Profile",
      path: `/advisor-portal/client-database/household-profile/${householdId}`,
    },
    {
      label: "Annual Review",
      path: `/advisor-portal/client-database/household-profile/${householdId}/annual-review`,
    },
  ];

  // Memoize the sidebar toggle handler and prevent unnecessary re-renders
  const handleSidebarToggle = useCallback(() => {
    if (isDemoMode && isDemoLocked) {
      console.log('🎯 Sidebar toggle prevented - demo is locked');
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  }, [isDemoMode, isDemoLocked]);

  // Memoize expensive computations
  const allAssets = useMemo(
    () =>
      getAllAssetsWithPrimaryResidence(
        formData?.assets || [],
        formData?.properties || [],
        formData?.primaryResidenceValue || 0,
      ),
    [formData?.assets, formData?.properties, formData?.primaryResidenceValue],
  );

  // Memoize the household name computation
  const getHouseholdName = useCallback(() => {
    if (isDemoMode) {
      return "Borer";
    }
    if (formData?.members?.[0]?.lastName) {
      return formData.members[0].lastName;
    }
    return "Unknown";
  }, [isDemoMode, formData]);

  // Memoize the filing status computation
  const getFilingStatus = useCallback(() => {
    if (
      formData?.taxInfo?.filingStatus &&
      typeof formData.taxInfo.filingStatus === "string"
    ) {
      return formData.taxInfo.filingStatus;
    }

    if (referenceDataState.taxFilingStatuses?.length > 0) {
      const status = referenceDataState.taxFilingStatuses.find(
        (s) =>
          s.id?.toString() ===
            formData?.taxInfo?.tax_filing_status_id?.toString() ||
          s.id?.toString() === formData?.taxInfo?.filingStatus?.toString(),
      );
      if (status?.name) {
        return status.name;
      }
    }

    return formData?.members?.length > 1 ? "Married Filing Jointly" : "Single";
  }, [
    formData?.taxInfo,
    formData?.members?.length,
    referenceDataState.taxFilingStatuses,
  ]);

  const handleExpenseUpdate = async (updatedExpense) => {
    try {
      const response = await apiConfig.put(
        `/advisor-portal/client-database/household-profile/${householdId}/expenses/${updatedExpense.expense_id}`,
        updatedExpense,
      );

      if (response.status === 200) {
        queryClient.setQueryData(
          ["annualReview", householdId, year, version],
          (oldData) => ({
            ...oldData,
            expenses: oldData.expenses.map((expense) =>
              expense.expense_id === updatedExpense.expense_id
                ? updatedExpense
                : expense,
            ),
          }),
        );
      }
    } catch (error) {
      logger.error("Error updating expense:", error);
    }
  };

  const handleExpenseAdd = async (newExpense) => {
    try {
      const response = await apiConfig.post(
        `/advisor-portal/client-database/household-profile/${householdId}/expenses`,
        newExpense,
      );

      if (response.status === 201) {
        const addedExpense = response.data;
        queryClient.setQueryData(
          ["annualReview", householdId, year, version],
          (oldData) => ({
            ...oldData,
            expenses: [...oldData.expenses, addedExpense],
          }),
        );
      }
    } catch (error) {
      logger.error("Error adding expense:", error);
    }
  };

  const handleExpenseEdit = (expense) => {
    logger.info("Preparing to edit expense:", expense);
    // Set up edit state or open modal as needed
  };

  const handleExpenseDelete = async (expense) => {
    try {
      const response = await apiConfig.delete(
        `/advisor-portal/client-database/household-profile/${householdId}/expenses/${expense.expense_id}`,
      );

      if (response.status === 200) {
        queryClient.setQueryData(
          ["annualReview", householdId, year, version],
          (oldData) => ({
            ...oldData,
            expenses: oldData.expenses.filter(
              (e) => e.expense_id !== expense.expense_id,
            ),
          }),
        );
      }
    } catch (error) {
      logger.error("Error deleting expense:", error);
    }
  };

  logger.info(
    "AnnualReviewProfile allAssets:",
    allAssets,
    Array.isArray(allAssets),
  );

  const highlightFilingStatus = getFilingStatus();

  function getBracketRate(taxableIncome, brackets, filingStatus) {
    const statusKey = {
      Single: "single",
      "Married Filing Jointly": "married",
      "Head of Household": "head",
      "Married Filing Separately": "marriedseparate",
    }[filingStatus];
    if (!statusKey || !brackets) return null;
    for (const bracket of brackets) {
      const range = bracket[statusKey];
      if (range) {
        const [min, max] = range.includes("or more")
          ? [parseFloat(range.replace(/[^\d.]/g, "")), Infinity]
          : range.split("to").map((s) => parseFloat(s.replace(/[^\d.]/g, "")));
        if (taxableIncome >= min && taxableIncome <= max) {
          return bracket.rate;
        }
      }
    }
    return null;
  }

  const selectedDeductionRow = taxData?.DEDUCTIONS?.[selectedTaxYear]?.find(
    (d) => d.status === highlightFilingStatus,
  );
  const highlightStandardDeduction = Boolean(selectedDeductionRow);
  const highlight65PlusDeduction =
    selectedDeductionRow &&
    parseFloat(selectedDeductionRow.over65.replace(/[$,]/g, "")) > 0;

  const [blindExemptionChecked, setBlindExemptionChecked] = useState(false);

  const handleBlindExemptionChange = (isChecked) => {
    setBlindExemptionChecked(isChecked);
  };

  const highlightBlindDeduction = blindExemptionChecked;

  let highlightBracketRate = null;
  try {
    const AGI = 0;
    const deduction65 =
      highlight65PlusDeduction && selectedDeductionRow
        ? parseFloat(selectedDeductionRow.over65.replace(/[$,]/g, ""))
        : 0;
    const standardDeduction = selectedDeductionRow
      ? parseFloat(selectedDeductionRow.standard.replace(/[$,]/g, ""))
      : 0;
    const taxableIncome = Math.max(0, AGI - (deduction65 + standardDeduction));
    highlightBracketRate = getBracketRate(
      taxableIncome,
      taxData?.TAX_BRACKETS?.[selectedTaxYear] || [],
      highlightFilingStatus,
    );
  } catch (e) {
    logger.error("Error calculating bracket rate:", e);
    highlightBracketRate = null;
  }

  const handleTogglePause = () => {
    if (isPaused) {
      resumeDemo();
    } else {
      pauseDemo();
    }
  };

  const handleStopDemo = () => {
    stopDemo();

    setShowRecordingToolbar(false);
  };

  const handleStartMeeting = () => {
    console.log('🎯 Starting meeting from briefing room');
    setShowBriefingRoom(false);     // hide the briefing room
    if (isDemoMode) {
      setShowCallModal(true);       // show the Call Confirmation modal
    } else {
      setShowCallModal(true);       // same, for real calls
    }
  };

  const handleCallConfirm = () => {
    console.log('📞 Call confirmed, starting demo');
    setShowCallModal(false);
    if (isDemoMode) {
      onAction({ type: 'CALL_CONFIRMED' });   // only here do we kick off the demo
      setShowRecordingToolbar(true);
      setCurrentModalType(null);
      setActiveModalData(null);
    }
  };

  const handleAddTranscriptLine = useCallback((line) => {
    // Instead of managing transcript locally, dispatch an action to add the line
    onAction({ type: 'ADD_TRANSCRIPT_LINE', data: line });
  }, [onAction]);

  const handleSaveNotes = async () => {
    // In demo mode, just log the save action
    if (isDemoMode) {
      console.log('📝 Demo mode: Simulating notes save:', {
        summary: notesSummary,
        updates: notesUpdates,
        tasks: notesTasks
      });
      return;
    }

    try {
      await apiConfig.api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/notes`,
        {
          summary: notesSummary,
          updates: notesUpdates,
          tasks: notesTasks,
        }
      );
      // Show success toast
    } catch (error) {
      logger.error("Error saving notes:", error);
      // Show error toast
    }
  };

  // Initialize data from API
  useEffect(() => {
    const fetchInitialData = async () => {
      // Skip API calls in demo mode
      if (isDemoMode) {
        if (location.state?.resumeDemo) {
          // Dispatch the resume action directly without wrapping
          onAction(location.state.resumeDemo);
          
          // Clean up resumeDemo from history to prevent re-firing
          const newState = { ...location.state };
          delete newState.resumeDemo;
          window.history.replaceState(newState, '', window.location.pathname);
        }
        return;
      }

      try {
        // Only make API calls if not in demo mode
        const response = await apiConfig.api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/notes`,
        );
        if (response.data) {
          setNotesSummary(response.data.summary || "");
          setNotesUpdates(response.data.updates || []);
          setNotesTasks(response.data.tasks || []);
        }
      } catch (error) {
        logger.error("Error fetching notes data:", error);
      }
    };

    fetchInitialData();
  }, [isDemoMode, location.state]);

  // Initialize demo data when call starts
  const handleCallStart = useCallback(() => {
    if (isDemoMode) {
      setShowCallModal(false);
      setSidebarCollapsed(false);

      // Initialize with demo data via context
      onAction({ type: 'SET_TRANSCRIPT', data: DEMO_TRANSCRIPT });
      setNotesSummary(DEMO_NOTES_SUMMARY);
      setNotesUpdates(DEMO_NOTES_UPDATES);
      setNotesTasks(DEMO_NOTES_TASKS);
      
      console.log('📝 Initialized demo data:', {
        transcriptLength: DEMO_TRANSCRIPT.length,
        summary: DEMO_NOTES_SUMMARY,
        updates: DEMO_NOTES_UPDATES,
        tasks: DEMO_NOTES_TASKS
      });
    } else {
      setShowCallModal(false);
      setIsRecording(true);
      setSidebarCollapsed(false);

      // Initialize with empty state for real calls
      onAction({ type: 'SET_TRANSCRIPT', data: [] });
      setNotesSummary("");
      setNotesUpdates([]);
      setNotesTasks([]);
    }
  }, [isDemoMode, setShowCallModal, setSidebarCollapsed, onAction]);

  // Enhanced modal handling
  const handleModalAction = useCallback((actionData) => {
    if (!actionData) return;

    try {
      switch (actionData.type) {
        case MODAL_TYPES.CALL_CONFIRMED:
          handleCallConfirm();
          break;
        case MODAL_TYPES.COMPLETE_DEMO:
          // Clean up demo state but preserve transcript
          onAction({ type: 'RESET_DEMO', preserveTranscript: true });
          setCurrentModalType(null);
          navigate('/advisor-portal/client-database');
          break;
        default:
          if (actionData.nextAction) {
            onAction(actionData.nextAction);
          }
          setCurrentModalType(null);
      }
    } catch (error) {
      console.error('Error handling modal action:', error);
      // Show error state in modal
      setCurrentModalType(prev => ({
        ...prev,
        error: 'An error occurred. Please try again.'
      }));
    }
  }, [handleCallConfirm, navigate, onAction]);

  // Update playback effect to be transcript-driven
  useEffect(() => {
    if (!isPlaying || isPaused || !isDemoMode || currentModalType) {
      logger.info("Skipping trigger check:", {
        isPlaying,
        isPaused,
        isDemoMode,
        hasModal: !!currentModalType
      });
      return;
    }
    // ... rest of existing playback effect code ...
  }, [isPlaying, isPaused, isDemoMode, currentModalType, transcript]);

  const handleOpenAllocations = () => {
    navigate(`/portfolio-management/household-allocations/${householdId}`, {
      state: { 
        resumeDemo: {
          type: 'RESUME_ALLOCATIONS',
          payload: {
            transcript,
            householdId
          }
        },
        isDemoMode: true,
        demoStep: 5,
        isPaused: true,
        preserveSidebar: true
      }
    });
  };

  // Add missing handler functions
  const handleHighlightSummary = (summary) => {
    setNotesSummary((prev) => prev + "\n\n" + summary);
  };

  const handleNotesSummaryChange = (newSummary) => {
    setNotesSummary(newSummary);
  };

  const handleNotesUpdatesChange = (newUpdates) => {
    setNotesUpdates(newUpdates);
  };

  const handleNotesTasksChange = (newTasks) => {
    setNotesTasks(newTasks);
  };

  // Update isDemoLocked when demo state changes
  useEffect(() => {
    if (isDemoMode) {
      setIsDemoLocked(isRecording && !isPaused);
    } else {
      setIsDemoLocked(false);
    }
  }, [isDemoMode, isRecording, isPaused]);

  // Add effect to monitor form state changes
  useEffect(() => {
    logger.info("🔍 Current form state:", {
      personalInfo: {
        maritalStatus: formState.personalInfo.maritalStatus,
        gender: formState.personalInfo.gender
      },
      taxInfo: formState.taxInfo,
      marriageInfo: formState.marriageInfo,
      address: formState.address
    });
  }, [formState]);

  // Add effect to monitor reference data state
  useEffect(() => {
    logger.info("🔍 Current reference data state:", {
      genderTypesCount: referenceDataState.genderTypes.length,
      maritalStatusCount: referenceDataState.maritalStatusTypes.length,
      taxFilingStatusCount: referenceDataState.taxFilingStatuses.length,
      filingStatesCount: referenceDataState.filingStates.length
    });
  }, [referenceDataState]);

  // Add missing variables
  const isSpouseSection = false; // Default to false since we handle spouse display differently
  const client = formData?.members?.[0] || {}; // Get primary client from members array

  // Add a data preparation effect
  const preparedTaxData = useMemo(() => {
    if (!taxData) return null;

    // Ensure year keys are strings
    const normalizedBrackets = Object.entries(taxData.TAX_BRACKETS || {}).reduce((acc, [year, data]) => {
      acc[String(year)] = data;
      return acc;
    }, {});

    const normalizedDeductions = Object.entries(taxData.DEDUCTIONS || {}).reduce((acc, [year, data]) => {
      acc[String(year)] = data;
      return acc;
    }, {});

    return {
      TAX_BRACKETS: normalizedBrackets,
      DEDUCTIONS: normalizedDeductions
    };
  }, [taxData]);

  // Debug logging for data shape
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Tax Data Shape:', {
        selectedYear: selectedTaxYear,
        yearType: typeof selectedTaxYear,
        bracketKeys: preparedTaxData?.TAX_BRACKETS ? Object.keys(preparedTaxData.TAX_BRACKETS) : [],
        deductionKeys: preparedTaxData?.DEDUCTIONS ? Object.keys(preparedTaxData.DEDUCTIONS) : [],
        hasBracketData: Boolean(preparedTaxData?.TAX_BRACKETS?.[String(selectedTaxYear)]?.length),
        hasDeductionData: Boolean(preparedTaxData?.DEDUCTIONS?.[String(selectedTaxYear)]?.length)
      });
    }
  }, [selectedTaxYear, preparedTaxData]);

  // Update the getCurrentMarginalRate function to use tax brackets directly
  const getCurrentMarginalRate = () => {
    if (!taxData?.TAX_BRACKETS?.[selectedTaxYear]) return null;
    
    const currentTaxableIncome = formData?.taxableIncome || 0;
    const filingStatus = formData?.taxInfo?.filingStatus ||
      (formData?.members?.length > 1 ? "Married Filing Jointly" : "Single");
    
    const statusKey = {
      "Single": "single",
      "Married Filing Jointly": "married",
      "Head of Household": "head",
      "Married Filing Separately": "marriedseparate"
    }[filingStatus];

    // Find the applicable bracket
    const brackets = taxData.TAX_BRACKETS[selectedTaxYear];
    for (const bracket of brackets) {
      const range = bracket[statusKey];
      if (!range) continue;
      
      if (range.includes("or more")) {
        return bracket.rate;
      }
      
      const [min, max] = range.split(" to ").map(val => 
        parseFloat(val.replace(/[^0-9.]/g, ''))
      );
      
      if (currentTaxableIncome >= min && currentTaxableIncome <= max) {
        return bracket.rate;
      }
    }
    
    return null;
  };

  return (
    <>
      <SidebarWrapper $demoActive={isDemoMode && isDemoLocked && !showCallModal}>
        <SidebarTemplate
          isExpanded={!sidebarCollapsed}
          onToggle={handleSidebarToggle}
          disabled={currentModalType === MODAL_TYPES.TRANSCRIPTION_INTRODUCTION}
          transcript={transcript}
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          onHighlightSummary={handleHighlightSummary}
          notesSummary={notesSummary}
          onNotesSummaryChange={handleNotesSummaryChange}
          notesUpdates={notesUpdates}
          onNotesUpdatesChange={handleNotesUpdatesChange}
          notesTasks={notesTasks}
          onNotesTasksChange={handleNotesTasksChange}
          onNotesSave={handleSaveNotes}
          onModalAction={handleModalAction}
          currentModalType={currentModalType}
          activeModalData={activeModalData}
        />
      </SidebarWrapper>

      <SubPageLayout
        title={`${getHouseholdName()} Household Review Profile`}
        subtitle={
          <button
            onClick={() => setIsMeetingDetailsOpen(true)}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            View Appointment Details
          </button>
        }
        breadcrumbItems={breadcrumbItems}
        isSidebarExpanded={!sidebarCollapsed}
        actionButton={
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateProfile(-1)}
                disabled={!canNavigateBack()}
                className={`p-2 rounded ${
                  canNavigateBack()
                    ? isDarkMode
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    : isDarkMode
                      ? 'text-gray-600 dark:text-gray-400 cursor-not-allowed'
                      : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>Version {version}</span>
              <button
                onClick={() => navigateProfile(1)}
                disabled={!canNavigateForward()}
                className={`p-2 rounded ${
                  canNavigateForward()
                    ? isDarkMode
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    : isDarkMode
                      ? 'text-gray-600 dark:text-gray-400 cursor-not-allowed'
                      : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
            <button
              onClick={openInNewWindow}
              className="btn-primary"
            >
              Open in New Window
            </button>
          </div>
        }
        className={currentModalType ? 'pointer-events-none' : 'pointer-events-auto'}
      >
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>Loading profile data...</p>
          </div>
        ) : error ? (
          <div className={`${isDarkMode ? 'bg-red-900/50' : 'bg-red-50'} border-l-4 border-red-500 p-4 mb-8`}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                  Error Loading Profile
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-700'} mt-1`}>
                  {typeof error === "string"
                    ? error
                    : "An error occurred while loading the profile data."}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* MeetingBriefingRoom */}
            <MeetingBriefingRoom
              isOpen={showBriefingRoom}
              onClose={() => {
                console.log('🎯 Closing briefing room');
                setShowBriefingRoom(false);
              }}
              meetingDetails={meetingDetails}
              formData={formData}
              pipelineData={pipelineData}
              getHouseholdName={getHouseholdName}
              onStartCall={handleStartMeeting}
              isDemoMode={isDemoMode}
            />

            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} rounded-lg shadow`}>
              <button
                onClick={() => setIsHouseholdInfoExpanded(!isHouseholdInfoExpanded)}
                className={`w-full flex items-center justify-between p-4 text-left ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                  Household Information
                </h2>
                {isHouseholdInfoExpanded ? (
                  <ChevronUpIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500 dark:text-gray-500'}`} />
                ) : (
                  <ChevronDownIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500 dark:text-gray-500'}`} />
                )}
              </button>

              {isHouseholdInfoExpanded && (
                <div className="px-6 pt-2 pb-6">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      {loading ? (
                        <div className="text-center py-4">Loading household information...</div>
                      ) : error ? (
                        <div className="text-red-500 text-center py-4">{error}</div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <MemberCard
                            data={formState.personalInfo}
                            onFieldChange={(field, value) =>
                              handleFieldChange(
                                "personalInfo",
                                field,
                                null,
                                value,
                              )
                            }
                            errors={{}}
                            prefixes={referenceDataState.prefixes}
                            suffixes={referenceDataState.suffixes}
                            genderTypes={referenceDataState.genderTypes}
                            householdId={householdId}
                            pipelineData={pipelineData.primaryMember}
                          />
                          <OverviewCard
                            maritalLabel={
                              referenceDataState.maritalStatusTypes.find(
                                (s) =>
                                  s.marital_status_id?.toString() ===
                                  formState.personalInfo.maritalStatus,
                              )?.marital_status_name || ""
                            }
                            taxLabel={
                              referenceDataState.taxFilingStatuses?.find(
                                (t) =>
                                  t.id?.toString() ===
                                  formState.taxInfo.filingStatus,
                              )?.name || ""
                            }
                            filingStateLabel={
                              referenceDataState.filingStates?.find(
                                (s) =>
                                  s.id?.toString() ===
                                  formState.taxInfo.filingState,
                              )?.name || ""
                            }
                            primaryAddress={`${formState.address.street || ""}, ${formState.address.city || ""}, ${formState.address.state || ""} ${formState.address.zipCode || ""}`}
                            mailingAddress={
                              formState.address.mailingAddress?.street
                                ? `${formState.address.mailingAddress.street || ""}, ${formState.address.mailingAddress.city || ""}, ${formState.address.mailingAddress.state || ""} ${formState.address.mailingAddress.zipCode || ""}`
                                : ""
                            }
                            marriageInfo={formState.marriageInfo}
                            familyCount={familyCount}
                            maritalStatusTypes={
                              referenceDataState.maritalStatusTypes
                            }
                            taxFilingStatusOptions={
                              referenceDataState.taxFilingStatuses || []
                            }
                            filingStateOptions={
                              referenceDataState.filingStates || []
                            }
                            onFieldChange={handleFieldChange}
                            formData={formState}
                          />
                          {formData?.members && formData.members.length > 1 && (
                            <MemberCard
                              data={formState.spouseInfo}
                              onFieldChange={(field, value) =>
                                handleFieldChange(
                                  "spouseInfo",
                                  field,
                                  null,
                                  value,
                                )
                              }
                              errors={{}}
                              prefixes={referenceDataState.prefixes}
                              suffixes={referenceDataState.suffixes}
                              genderTypes={referenceDataState.genderTypes}
                              householdId={householdId}
                              pipelineData={pipelineData.spouse}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} rounded-lg shadow mb-6`}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    Cash Flow Overview
                  </h2>
                  <CustomBarIcon />
                </div>
                <div className="flex gap-4 w-full">
                  <div className="w-1/2">
                    <div className="overflow-y-auto max-h-[600px]">
                      <IncomeAnalysisTable
                        incomes={
                          formData?.incomes
                            ?.filter((income) => {
                              const amount = parseFloat(
                                income.gross_amount || income.amount || 0,
                              );
                              return amount > 0;
                            })
                            .map((income) => {
                              const isCustomFrequency =
                                income.custom_frequency_days &&
                                income.custom_frequency_days >= 3 &&
                                income.custom_frequency_days <= 365;

                              // For custom frequencies, we'll create a custom frequency object
                              const customFrequency = isCustomFrequency
                                ? {
                                    id: "custom",
                                    name: `${income.custom_frequency_days} times per year`,
                                    multiplier: parseInt(
                                      income.custom_frequency_days,
                                      10,
                                    ),
                                  }
                                : null;

                              // Find the frequency in the reference data, or use custom
                              const frequencyInfo =
                                customFrequency ||
                                referenceData?.frequencies?.find(
                                  (f) =>
                                    f.frequency_id === income.frequency_id,
                                );

                              const incomeTypeInfo = INCOME_TYPES_DATA.find(
                                (t) =>
                                  t.income_type_id === income.income_type_id,
                              );

                              return {
                                id: income.client_income_id,
                                client_income_id: income.client_income_id,
                                user_id: income.user_id,
                                member_id: income.user_id,
                                memberId: income.user_id,
                                income_type_id:
                                  income.income_type_id?.toString(),
                                amount: income.gross_amount || income.amount,
                                frequency: isCustomFrequency
                                  ? "custom"
                                  : income.frequency_id,
                                frequency_id: isCustomFrequency
                                  ? "custom"
                                  : income.frequency_id,
                                frequency_name: isCustomFrequency
                                  ? `${income.custom_frequency_days} times per year`
                                  : frequencyInfo?.frequency_name,
                                frequency_multiplier: isCustomFrequency
                                  ? parseInt(income.custom_frequency_days, 10)
                                  : frequencyInfo?.frequency_multiplier,
                                income_type_name:
                                  incomeTypeInfo?.income_type_name ||
                                  "Other Income",
                                start_date: income.start_date,
                                end_date: income.end_date,
                                custom_frequency_days:
                                  income.custom_frequency_days,
                                memberName:
                                  formData?.members?.find(
                                    (m) => m.userId === income.user_id,
                                  )?.firstName +
                                  " " +
                                  formData?.members?.find(
                                    (m) => m.userId === income.user_id,
                                  )?.lastName,
                              };
                            }) || []
                        }
                        members={
                          formData?.members?.map((member) => ({
                            id: member.userId,
                            name: `${member.firstName} ${member.lastName}`,
                          })) || []
                        }
                        onSave={handleIncomeUpdate}
                        frequencyData={
                          referenceData?.frequencies?.map((f) => ({
                            frequency_id: f.frequency_id,
                            frequency_name: f.frequency_name,
                            frequency_multiplier: f.frequency_multiplier,
                          })) || []
                        }
                        incomeTypesData={INCOME_TYPES_DATA}
                        deductionTypes={referenceData?.deductionTypes || []}
                        getIncomeTypeName={(id) => {
                          const incomeType = INCOME_TYPES_DATA.find(
                            (t) => t.income_type_id === parseInt(id),
                          );
                          return (
                            incomeType?.income_type_name || "Other Income"
                          );
                        }}
                        isEditMode={true}
                        config={{
                          showDates: true,
                          editable: true,
                          title: "Income Analysis",
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-1/2">
                    <div className="overflow-y-auto max-h-[600px]">
                      <ExpenseAnalysisTable
                        expenses={
                          formData?.expenses?.filter(
                            (expense) => parseFloat(expense.amount || 0) > 0,
                          ) || []
                        }
                        members={
                          formData?.members?.map((member) => ({
                            id: member.userId,
                            name: `${member.firstName} ${member.lastName}`,
                          })) || []
                        }
                        onSave={handleExpenseUpdate}
                        onAddExpense={handleExpenseAdd}
                        onEditExpense={handleExpenseEdit}
                        onDeleteExpense={handleExpenseDelete}
                        isEditMode={true}
                        getExpenseTypeName={(id) => {
                          const expenseType =
                            referenceData?.expenseTypes?.find(
                              (t) => t.expense_type_id === parseInt(id),
                            );
                          return (
                            expenseType?.expense_type_name || "Other Expense"
                          );
                        }}
                        expenseTypes={referenceData?.expenseTypes || []}
                        frequencyData={
                          referenceData?.frequencies?.map((f) => ({
                            frequency_id: f.frequency_id,
                            frequency_name: f.frequency_name,
                            frequency_multiplier: f.frequency_multiplier,
                          })) || []
                        }
                        config={{
                          showDates: true,
                          editable: true,
                          title: "Expense Analysis",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Balance Sheet Section */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} rounded-lg shadow mb-6`}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    Balance Sheet
                  </h2>
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10H9m3-5h3m-6 0h0M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5c0-1.1.9-2 2-2z" />
                  </svg>
                </div>
                <div className="grid grid-cols-12 gap-6">
                  {/* Liabilities Card - Left */}
                  <div className="col-span-12 lg:col-span-4 flex min-w-0">
                    <div className={`${CARD_BASE} flex flex-col justify-between flex-1 h-full min-h-[300px] min-w-0 border-l-4 border-red-600 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <HouseholdDebtAnalysis
                        debtsData={formData?.debts || []}
                        members={formData?.members || []}
                        assetsData={{
                          assets: allAssets,
                          assetTypes: referenceData?.assetTypes || [],
                          taxQualifications: referenceData?.taxQualifications || [],
                          accountTypes: referenceData?.accountTypes || [],
                          financialInstitutions: referenceData?.financialInstitutions || [],
                          properties: formData?.properties || [],
                          members: formData?.members || [],
                          ownerTypes: referenceData?.ownerTypes || []
                        }}
                        primaryResidenceValueData={formData?.primaryResidenceValue || 0}
                        onSave={handleDebtUpdate}
                        onSaveAssets={handleAssetUpdate}
                      />
                    </div>
                  </div>

                  {/* Assets Card - Middle */}
                  <div className="col-span-12 lg:col-span-4 flex min-w-0">
                    <div className={`${CARD_BASE} flex flex-col justify-between flex-1 h-full min-h-[300px] min-w-0 border-l-4 border-blue-600 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <HouseholdAssetAnalysis
                        assetsData={{
                          assets: allAssets,
                          assetTypes: referenceData?.assetTypes || [],
                          taxQualifications: referenceData?.taxQualifications || [],
                          accountTypes: referenceData?.accountTypes || [],
                          financialInstitutions: referenceData?.financialInstitutions || [],
                          properties: formData?.properties || [],
                          members: formData?.members || [],
                          ownerTypes: referenceData?.ownerTypes || []
                        }}
                        primaryResidenceValueData={formData?.primaryResidenceValue || 0}
                        onSave={handleAssetUpdate}
                      />
                    </div>
                  </div>

                  {/* Net Worth Card - Right */}
                  <div className="col-span-12 lg:col-span-4 flex min-w-0">
                    <div className={`${CARD_BASE} flex flex-col justify-between flex-1 h-full min-h-[300px] min-w-0 border-l-4 border-green-600 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <HouseholdNetWorthCard
                        assets={getAllAssetsWithPrimaryResidence(
                          allAssets,
                          formData?.properties || [],
                          formData?.properties?.[0]?.residence_value || formData?.primaryResidenceValue || 0
                        )}
                        liabilities={formData?.debts || []}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Analysis Section */}
            <div id="tax-analysis-section" className={`${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} rounded-lg shadow mb-2`}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                    {selectedTaxYear} Household Tax Analysis
                  </h2>
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-6">
                  {/* Left Column - Tax Analysis */}
                  <div className="flex flex-col h-full min-h-0 w-full">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-none h-full w-full">
                      <div className="flex-1 min-h-0 overflow-auto w-full p-4">
                        <HouseholdTaxAnalysis
                          year={parseInt(selectedTaxYear)}
                          taxBrackets={taxData?.TAX_BRACKETS?.[selectedTaxYear]}
                          deductionsData={taxData?.DEDUCTIONS?.[selectedTaxYear]}
                          birthdates={{
                            clientBirthdate: formData?.members?.[0]?.dateOfBirth || null,
                            spouseBirthdate: formData?.members?.[1]?.dateOfBirth || null,
                          }}
                          incomeData={formData?.incomes || []}
                          onTaxableIncomeChange={handleFieldChange}
                          frequencyData={referenceData?.frequencies
                            ?.filter((f) => !f.is_other)
                            .map((f) => ({
                              frequency_id: f.frequency_id,
                              frequency_name: f.frequency_name,
                              frequency_multiplier: f.frequency_multiplier,
                            })) || []}
                          onBlindExemptionChange={handleBlindExemptionChange}
                          isDarkMode={isDarkMode}
                          initialTaxFilingStatus={
                            formData?.taxInfo?.filingStatus ||
                            (formData?.members?.length > 1 ? "Married Filing Jointly" : "Single")
                          }
                          hideTitle={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Brackets and Deductions */}
                  <div className="flex flex-col h-full min-h-0 w-full">
                    {/* Tax Bracket Information */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-none mb-4">
                      <div className="flex items-center justify-between px-4 py-2 border-b dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Tax Bracket Information
                        </h3>
                        <select
                          value={selectedTaxYear}
                          onChange={(e) => setSelectedTaxYear(Number(e.target.value))}
                          className="text-xs border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        >
                          {Object.keys(taxData?.TAX_BRACKETS || {})
                            .sort((a, b) => b - a)
                            .map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                      </div>
                      <div className="w-full p-4">
                        <TaxBracketInformation
                          defaultYear={selectedTaxYear}
                          contentOnly={true}
                          hideTitle={true}
                          hideYearDropdown={true}
                          highlightFilingStatus={
                            formData?.taxInfo?.filingStatus ||
                            (formData?.members?.length > 1 ? "Married Filing Jointly" : "Single")
                          }
                          highlightBracketRate={getCurrentMarginalRate()}
                          highlightFullRow={false}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    </div>

                    {/* Deductions Table */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-none">
                      <div className="flex items-center px-4 py-2 border-b dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Standard Deductions & Exemptions
                        </h3>
                      </div>
                      <div className="w-full p-4">
                        <DeductionsTable
                          defaultYear={selectedTaxYear}
                          contentOnly={true}
                          hideYearDropdown={true}
                          isDarkMode={isDarkMode}
                          highlightFilingStatus={
                            formData?.taxInfo?.filingStatus ||
                            (formData?.members?.length > 1 ? "Married Filing Jointly" : "Single")
                          }
                          highlightStandardDeduction={true}
                          highlight65PlusDeduction={
                            formData?.members?.some(m => calculateAge(m.dateOfBirth) >= 65)
                          }
                          highlightBlindDeduction={blindExemptionChecked}
                          highlightFullRow={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`text-center py-4 mt-2`}>
              <button
                onClick={handleSaveProfile}
                className="btn-primary"
              >
                Save Profile
              </button>
              <button
                onClick={() => {
                  navigate(
                    `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}/post-review`,
                  );
                }}
                className="btn-secondary ml-4"
              >
                Post Review Action Items
              </button>
            </div>
          </div>
        )}
      </SubPageLayout>

      {/* Modals - kept as direct children of the root */}
      <Modal
        isOpen={isMeetingDetailsOpen}
        onClose={() => setIsMeetingDetailsOpen(false)}
        size="sm"
      >
        <div className="p-6">
          <h2 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            Meeting Details
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Date
              </label>
              <p className={`mt-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{meetingDetails.date}</p>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Time
              </label>
              <p className={`mt-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{meetingDetails.time}</p>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Video Conference Link
              </label>
              <a
                href={meetingDetails.videoLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-1 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                {meetingDetails.videoLink}
              </a>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Status
              </label>
              <p className={`mt-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{meetingDetails.status}</p>
            </div>
          </div>
        </div>
      </Modal>

      <CallConfirmationModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        onConfirm={handleCallConfirm}
        client={formData?.members?.[0]}
        className="z-[10001]"
      />

      {currentModalType && currentModalType !== "DEMO_COMPLETE" && (
        <DemoModal
          sidebarWidth={!sidebarCollapsed ? 380 : 0}
          {...MODAL_TEMPLATES[currentModalType]}
          {...activeModalData}
          onAction={handleModalAction}
          backdropProps={{ style: { zIndex: 1000 } }}
          modalProps={{ style: { zIndex: 1200 } }}
          onClose={() => {
            logger.info("Modal close triggered");
            setCurrentModalType(null);
            setActiveModalData(null);
            setCurrentIndex((prev) => {
              const nextIndex = prev + 1;
              if (isDemoMode && DEMO_TRANSCRIPT[nextIndex]) {
                const nextLine = DEMO_TRANSCRIPT[nextIndex];
                onAction({ 
                  type: 'ADD_TRANSCRIPT_LINE', 
                  data: nextLine 
                });
              }
              return nextIndex;
            });
            setTimeout(() => {
              setIsPaused(false);
              setIsPlaying(true);
            }, 100);
          }}
        />
      )}

      {currentModalType === "DEMO_COMPLETE" && (
        <Dialog open={true} onClose={() => {}} className="relative z-[10000]">
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className={`mx-auto max-w-sm rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-surface-default dark:bg-surface-default'} p-6`}>
              <Dialog.Title className="text-xl font-semibold text-green-600 mb-4">
                Demo Completed! 🎉
              </Dialog.Title>

              <div className="space-y-4">
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  You've successfully completed the Annual Review demo. Great
                  job!
                </p>

                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500 dark:text-gray-500'}`}>
                    Completed on:{" "}
                    {new Date(demoCompletionTime).toLocaleString()}
                  </p>
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}

      {showRecordingToolbar && (
        <DemoToolbar
          isPaused={isPaused}
          onTogglePause={handleTogglePause}
          onStop={handleStopDemo}
        />
      )}
    </>
  );
};

// Export the component directly
const AnnualReviewProfileWithDemo = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeContextProvider>
      <StyledThemeProvider>
        <AnnualReviewProfile />
      </StyledThemeProvider>
    </ThemeContextProvider>
  </QueryClientProvider>
);

export default AnnualReviewProfileWithDemo;